import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CrediusAffiliazioni } from "../components/CrediusAffiliazioni";
import { CrediusContabilita } from "../components/CrediusContabilita";
import { CrediusRicorrenze } from "../components/CrediusRicorrenze";
import { CrediusDashboard } from "../components/CrediusDashboard";
import { CrediusOnboarding } from "../components/CrediusOnboarding";
import { getBandCredius, getPillCredius } from "../components/SharedCard";
import { Scale, Gavel, FileText, Handshake, PlusCircle, Search, ChevronRight, MessageCircle, Loader2, AlertCircle, ShieldAlert, RefreshCw, Receipt, Trash2, UserSquare2, UserCheck, Users, Euro } from "lucide-react";
import { toast } from "sonner";

// Iter122k33 · Colori sincronizzati con getBandCredius (SharedCard) per identificare
// visivamente la fase della pratica sia sul badge in-card sia sulla banda sinistra.
// La mappa vera vive in SharedCard.js (CREDIUS_STATO_PILL) per essere condivisa con la dashboard.

// Iter122k33 · Colori distintivi per ciascuna tab (attiva). Le tab che filtrano
// per uno specifico stato ereditano il colore del bordo laterale della pratica
// (getBandCredius) per una lettura visiva immediata.
const TAB_ACTIVE_STYLE = {
  dashboard:      "bg-indigo-100 border-indigo-500 text-indigo-800",
  mie_pratiche:   "bg-indigo-100 border-indigo-500 text-indigo-800",
  da_affidare:    "bg-amber-100 border-amber-500 text-amber-800",       // = border pratica "Da affidare"
  preventivo:     "bg-indigo-100 border-indigo-500 text-indigo-800",    // = border pratica "Preventivo"
  stragiudiziali: "bg-sky-100 border-sky-500 text-sky-800",
  giudiziali:     "bg-purple-100 border-purple-500 text-purple-800",
  sinistri:       "bg-rose-100 border-rose-500 text-rose-800",
  contabilita:    "bg-emerald-100 border-emerald-500 text-emerald-800",
  ricorrenze:     "bg-cyan-100 border-cyan-500 text-cyan-800",
  affiliazioni:   "bg-slate-200 border-slate-500 text-slate-800",
};

function PraticaCard({ p, onOpen, canDelete, onDelete, q = "" }) {
  const bordoStato = getBandCredius(p);
  // Iter123k229 · riepilogo finanziario pratica (solo settore Recupero crediti)
  const fin = p.recuperi_riepilogo || null;
  const hasFinanziari = p.settore === "Recupero crediti" && fin && (fin.totale_debito || 0) > 0;
  const fmtEur = (n) => (n || 0).toLocaleString("it-IT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  });
  const clientiStr = (p.clienti || []).map((c) => c.nome).join(", ");
  const contropartiStr = (p.controparti || []).map((c) => c.nome).join(", ");
  return (
    <div data-testid={`credius-pratica-${p.numero}`}
      className={`relative w-full bg-white rounded-xl border-2 border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all ${bordoStato} hover:border-l-[12px]`}>
      <button type="button" onClick={() => onOpen(p)} className="w-full text-left p-4 pr-12">
        {/* Riga 1: numero + badge stato + settore */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-bold text-indigo-900 text-sm">{highlightMatch(p.numero, q)}</span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${getPillCredius(p.stato)}`}>{p.stato}</span>
          {p.settore && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{highlightMatch(p.settore, q)}</span>}
          {p.incarico_stato === "In attesa di accettazione" && (
            <span data-testid={`credius-incarico-attesa-${p.numero}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-300">Incarico da accettare</span>
          )}
          {p.incarico_stato === "Rifiutato" && (
            <span data-testid={`credius-incarico-rifiutato-${p.numero}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300">Incarico rifiutato</span>
          )}
          {p.trasformata_in_numero && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">→ {p.trasformata_in_numero}</span>}
          {p.messaggi_non_letti > 0 && (
            <span data-testid={`credius-unread-${p.numero}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
              <MessageCircle className="w-3 h-3" /> {p.messaggi_non_letti}
            </span>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400 ml-auto shrink-0" />
        </div>

        {/* Titolo pratica (oggetto) - più grande e scuro per leggibilità rapida */}
        <div className="mt-2 font-bold text-slate-900 line-clamp-2 text-[15px] leading-snug">
          {highlightMatch(p.oggetto, q)}
        </div>

        {/* Dati essenziali: icone + due colonne su desktop, impilati su mobile */}
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div className="flex items-start gap-1.5 min-w-0">
            <UserCheck className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">Avvocato</div>
              <div className="text-slate-800 line-clamp-1">
                {p.avvocato_nome ? highlightMatch(p.avvocato_nome, q) : <span className="italic text-slate-400">non assegnato</span>}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-1.5 min-w-0">
            <Users className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wide">Cliente{(p.clienti || []).length > 1 ? "i" : ""}</div>
              <div className="text-slate-800 font-medium line-clamp-1">
                {clientiStr ? highlightMatch(clientiStr, q) : <span className="italic text-slate-400">—</span>}
              </div>
            </div>
          </div>
          {contropartiStr && (
            <div className="flex items-start gap-1.5 min-w-0 sm:col-span-2">
              <Gavel className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase font-bold text-rose-700 tracking-wide">Controparte{(p.controparti || []).length > 1 ? "i" : ""}</div>
                <div className="text-slate-800 line-clamp-1">
                  {highlightMatch(contropartiStr, q)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Riepilogo finanziario (solo Recupero crediti) */}
        {hasFinanziari && (
          <div data-testid={`credius-pratica-fin-${p.numero}`}
            className="mt-2.5 flex flex-wrap gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 border border-slate-200">
              <Euro className="w-3 h-3 text-slate-500" />
              <span className="font-semibold text-slate-500">Debito</span>
              <span className="font-bold text-slate-800">{fmtEur(fin.totale_debito)}</span>
            </span>
            {(fin.totale_incassato || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
                <span className="font-semibold text-emerald-700">Incassato</span>
                <span className="font-bold text-emerald-900">{fmtEur(fin.totale_incassato)}</span>
              </span>
            )}
            {(fin.totale_residuo || 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200">
                <span className="font-semibold text-amber-700">Residuo</span>
                <span className="font-bold text-amber-900">{fmtEur(fin.totale_residuo)}</span>
              </span>
            )}
          </div>
        )}

        {p.avanzamento && (
          <div className="mt-2 text-[12px] text-indigo-700 italic line-clamp-1 border-l-2 border-indigo-200 pl-2">
            {p.avanzamento}
          </div>
        )}
      </button>
      {canDelete && (
        <button type="button" data-testid={`credius-pratica-elimina-${p.numero}`}
          onClick={(e) => { e.stopPropagation(); onDelete(p); }}
          title="Elimina pratica (soft-delete)"
          className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-400 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function Credius() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isAvvocato = user?.role === "fornitore" && user?.is_avvocato;
  const isClient = !!user?.is_credius_client && !isAdmin && !isAvvocato;
  // Iter122i · default tab · admin apre subito su "Da affidare" per intervenire sulle pratiche in attesa.
  const [tab, setTab] = useState(isClient ? "mie_pratiche" : (isAdmin ? "da_affidare" : "stragiudiziali"));
  const [pratiche, setPratiche] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/credius/pratiche").then((r) => setPratiche(r.data)).catch(() => setPratiche([]));
  // Iter122k142 · badge scadenze PDR (solo admin/avvocato)
  const [scadenze, setScadenze] = useState(null);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isClient) return;
    api.get("/credius/pdr/scadenze")
      .then((r) => setScadenze(r.data))
      .catch(() => {});
  }, [isClient]);

  // Iter122i · scroll a inizio pagina quando l'utente cambia scheda (importante su mobile).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [tab]);

  const tabs = isClient
    ? [{ key: "mie_pratiche", label: "Le mie pratiche", icon: Scale }]
    : [
        // Iter122av · Dashboard sintetica + solleciti
        { key: "dashboard", label: "Dashboard", icon: RefreshCw, adminOnly: true },
        { key: "da_affidare", label: "Da affidare", icon: AlertCircle, adminOnly: true },
        { key: "preventivo", label: "Preventivo", icon: FileText, adminOnly: true },
        { key: "stragiudiziali", label: "Stragiudiziale", icon: FileText },
        { key: "giudiziali", label: "Giudiziale", icon: Gavel },
        { key: "sinistri", label: "Sinistri", icon: ShieldAlert },
        { key: "contabilita", label: "Contabilità · Notule", icon: Receipt, adminOnly: true },
        { key: "ricorrenze", label: "Ricorrenze", icon: RefreshCw, adminOnly: true },
        ...(isAdmin ? [{ key: "affiliazioni", label: "Convenzioni", icon: Handshake }] : []),
      ].filter((t) => !t.adminOnly || isAdmin);

  const TIPO_BY_TAB = { stragiudiziali: "stragiudiziale", giudiziali: "giudiziale", sinistri: "sinistro" };
  const tipoTab = TIPO_BY_TAB[tab];
  const isDaAffidareTab = tab === "da_affidare";
  const isPreventivoTab = tab === "preventivo";
  const isMiePraticheTab = tab === "mie_pratiche";
  const isContabilitaTab = tab === "contabilita";
  const isRicorrenzeTab = tab === "ricorrenze";
  const isListaPratiche = tipoTab || isDaAffidareTab || isPreventivoTab || isMiePraticheTab;

  // Iter122k106 · Contatore per tab: somma i messaggi non letti delle pratiche
  // che rientrano nello stesso "gruppo" del tab (Stragiudiziale/Giudiziale/Sinistri/Da affidare/Preventivo/Mie pratiche).
  // I tab "Dashboard" e "Ricorrenze" restano senza badge (non hanno chat). "Contabilità" somma i non-letti su tutte
  // le pratiche (le notule si aprono da ogni tipo di pratica). "Convenzioni" resta senza badge.
  const unreadByTab = (() => {
    const out = { stragiudiziali: 0, giudiziali: 0, sinistri: 0,
                   da_affidare: 0, preventivo: 0, mie_pratiche: 0, contabilita: 0 };
    for (const p of (pratiche || [])) {
      const n = Number(p.messaggi_non_letti || 0);
      if (!n) continue;
      out.contabilita += n; // globale per contabilità
      if (isClient) { out.mie_pratiche += n; continue; }
      if (p.stato === "Da affidare") { out.da_affidare += n; continue; }
      if (p.stato === "Preventivo") { out.preventivo += n; continue; }
      if (p.tipo === "stragiudiziale") out.stragiudiziali += n;
      else if (p.tipo === "giudiziale") out.giudiziali += n;
      else if (p.tipo === "sinistro") out.sinistri += n;
    }
    return out;
  })();
  const list = (pratiche || [])
    .filter((p) => {
      if (isMiePraticheTab) return true;  // cliente vede tutte le sue pratiche in un unico tab
      if (isDaAffidareTab) return p.stato === "Da affidare";
      if (isPreventivoTab) return p.stato === "Preventivo";
      return p.tipo === tipoTab && p.stato !== "Da affidare" && p.stato !== "Preventivo";
    })
    .filter((p) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      const testo = [p.numero, p.oggetto, p.avvocato_nome, p.settore,
        ...(p.clienti || []).map((c) => c.nome), ...(p.controparti || []).map((c) => c.nome)].join(" ").toLowerCase();
      return testo.includes(s);
    });

  return (
    <div data-testid="credius-page" className="max-w-5xl mx-auto">
      <CrediusOnboarding />
      {scadenze && (scadenze.totali?.scadute || scadenze.totali?.imminenti) > 0 && (
        <div className="mb-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex flex-wrap items-center gap-3"
             data-testid="credius-pdr-scadenze-banner">
          <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900">
              Piani di rientro · rate da monitorare
            </div>
            <div className="text-xs text-amber-800">
              {scadenze.totali.scadute > 0 && (
                <span data-testid="credius-pdr-scadute-count">
                  <b>{scadenze.totali.scadute}</b> rate <b>scadute</b> non incassate
                </span>
              )}
              {scadenze.totali.scadute > 0 && scadenze.totali.imminenti > 0 && " · "}
              {scadenze.totali.imminenti > 0 && (
                <span data-testid="credius-pdr-imminenti-count">
                  <b>{scadenze.totali.imminenti}</b> in scadenza nei prossimi 7 giorni
                </span>
              )}
            </div>
          </div>
          <details className="w-full mt-2">
            <summary className="cursor-pointer text-xs font-semibold text-amber-900 hover:underline">
              Vedi elenco
            </summary>
            <div className="mt-2 space-y-1 max-h-60 overflow-auto">
              {[...(scadenze.scadute || []), ...(scadenze.imminenti || [])].map((r, i) => (
                <button key={i} type="button"
                  data-testid={`credius-pdr-scad-item-${i}`}
                  onClick={() => navigate(`/credius/pratiche/${r.pratica_id}`)}
                  className="w-full text-left flex flex-wrap items-center gap-2 text-xs px-2 py-1 rounded hover:bg-amber-100">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.giorni < 0 ? "bg-red-600 text-white" : "bg-amber-600 text-white"}`}>
                    {r.giorni < 0 ? `${-r.giorni}g fa` : `T-${r.giorni}g`}
                  </span>
                  <span className="font-mono text-slate-500">{r.pratica_numero}</span>
                  <span className="font-semibold truncate flex-1">{r.debitore}</span>
                  <span className="text-slate-600">Rata {r.n}/{r.totale_rate}</span>
                  <span className="font-bold">€ {Number(r.importo).toFixed(2)}</span>
                </button>
              ))}
            </div>
          </details>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div className="w-12 h-12 rounded-2xl bg-indigo-900 flex items-center justify-center shrink-0">
          <Scale className="w-7 h-7 text-[#D4AF5A]" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-950">Credius</h1>
          <p className="text-sm text-slate-500">{isClient ? "Le mie pratiche legali seguite dallo Studio" : "Gestione delle pratiche legali — stragiudiziali e giudiziali"}</p>
        </div>
        {isAdmin && (
          <Button data-testid="credius-nuova-btn" onClick={() => navigate("/credius/nuova")}
            className="ml-auto bg-indigo-900 hover:bg-indigo-950 h-11">
            <PlusCircle className="w-5 h-5 mr-2" /> Nuova pratica
          </Button>
        )}
      </div>

      <div className="flex gap-1 mt-5 mb-5 border-b-2 border-slate-200 overflow-x-auto">
        {tabs.map((t) => {
          const Icon = t.icon;
          const badge = unreadByTab[t.key] || 0;
          return (
            <button key={t.key} type="button" data-testid={`credius-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`relative flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 font-semibold text-xs sm:text-sm whitespace-nowrap border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${
                tab === t.key
                  ? (TAB_ACTIVE_STYLE[t.key] || "bg-sky-100 border-sky-500 text-sky-800")
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {t.label}
              {badge > 0 && (
                <span data-testid={`credius-tab-badge-${t.key}`}
                  className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </button>
          );
        })}
        {/* Iter122k51 · Scorciatoia ad Anagrafiche (utile su mobile dove la sidebar è compressa)
            Iter123k168 · rimosso ml-auto su mobile (creava tab isolato scorrere-a-destra confuso) */}
        {isAdmin && (
          <button type="button" data-testid="credius-tab-anagrafica-shortcut"
            onClick={() => navigate("/credius/anagrafica")}
            className="sm:ml-auto flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 font-semibold text-xs sm:text-sm whitespace-nowrap border-b-[3px] -mb-0.5 rounded-t-md border-transparent text-slate-500 hover:text-indigo-800 hover:bg-indigo-50 transition-colors">
            <UserSquare2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Anagrafiche
          </button>
        )}
      </div>

      {tab === "contabilita" && isAdmin && <CrediusContabilita isAdmin={isAdmin} />}
      {tab === "ricorrenze" && isAdmin && <CrediusRicorrenze />}
      {tab === "affiliazioni" && isAdmin && <CrediusAffiliazioni />}
      {tab === "dashboard" && isAdmin && <CrediusDashboard pratiche={pratiche || []} />}
      {(tipoTab || isDaAffidareTab || isPreventivoTab || isMiePraticheTab) && !isContabilitaTab && !isRicorrenzeTab && (
        <>
          <div className="relative mb-4">
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="credius-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per numero, oggetto, avvocato, cliente o controparte..." className="h-12 pl-10" />
          </div>
          {isMiePraticheTab && (
            <div className="mb-3 p-3 rounded-lg bg-indigo-50 border-2 border-indigo-200 text-sm text-indigo-900 flex items-start gap-2" data-testid="credius-mie-pratiche-hint">
              <Scale className="w-5 h-5 text-indigo-700 shrink-0 mt-0.5" />
              <span>Vista in sola lettura delle pratiche legali che ti riguardano. Per aggiornamenti puoi comunicare con lo Studio dalla chat presente in ogni pratica.</span>
            </div>
          )}
          {isDaAffidareTab && (
            <div className="mb-3 p-3 rounded-lg bg-slate-100 border-2 border-slate-300 text-sm text-slate-700 flex items-start gap-2" data-testid="credius-da-affidare-hint">
              <AlertCircle className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
              <span>Pratiche salvate senza affido — clicca su una per assegnarla ad un avvocato convenzionato.</span>
            </div>
          )}
          {isPreventivoTab && (
            <div className="mb-3 p-3 rounded-lg bg-sky-50 border-2 border-sky-300 text-sm text-sky-900 flex items-start gap-2" data-testid="credius-preventivo-hint">
              <FileText className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
              <span>Pratiche in fase di <b>preventivazione</b> per contenziosi esterni. Il preventivo dell&apos;avvocato verrà sottoposto al cliente per la sottoscrizione.</span>
            </div>
          )}
          {pratiche === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-800" /></div>
          ) : list.length === 0 ? (
            <div data-testid="credius-empty" className="text-center py-16 text-slate-500">
              <Scale className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              {isMiePraticheTab ? "Nessuna pratica legale al momento." :
                isDaAffidareTab ? "Nessuna pratica in attesa di affido." :
                isPreventivoTab ? "Nessuna pratica in fase di preventivo." :
                `Nessuna pratica ${tipoTab} presente.`}
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((p) => (
                <PraticaCard key={p.id} p={p} q={q} onOpen={(x) => navigate(`/credius/${x.id}`)}
                  canDelete={isAdmin}
                  onDelete={async (x) => {
                    if (!window.confirm(`Eliminare la pratica ${x.numero}?\n\nOggetto: ${x.oggetto}\n\nLa cancellazione è logica (soft-delete): potrà essere recuperata dal DB in caso di errore.`)) return;
                    try {
                      await api.delete(`/credius/pratiche/${x.id}`);
                      toast.success(`Pratica ${x.numero} eliminata`);
                      load();
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita");
                    }
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
